import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GlobalStyles from "./styles/GlobalStyles";
import { theme } from "./styles/theme";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { BookingModalProvider } from "./hooks/useNewBookingModal";
import ScrollUp from "./components/user/ScrollUp/ScrollUp";
import ProtectedRoute from "./components/user/ProtectedRoute/ProtectedRoute";
import AdminProtectedRoute from "./components/admin/ProtectedRoute/AdminProtectedRoute";

const Home = React.lazy(() => import("./pages/user/Home/Home"));
const Contact = React.lazy(() => import("./pages/user/Contact/Contact"));
const Services = React.lazy(() => import("./pages/user/Services/Services"));
const Products = React.lazy(() => import("./pages/user/Products/Products"));
const ProductDetail = React.lazy(() => import("./pages/user/ProductDetail/ProductDetail"));
const Comparison = React.lazy(() => import("./pages/user/Comparison/Comparison"));
const Cart = React.lazy(() => import("./pages/user/Cart/Cart"));
const Checkout = React.lazy(() => import("./pages/user/Checkout/Checkout"));
const OrderConfirmation = React.lazy(() => import("./pages/user/OrderConfirmation/OrderConfirmation"));
const UserAuth = React.lazy(() => import("./pages/user/Auth/Auth"));
const MyOrders = React.lazy(() => import("./pages/user/MyOrders/MyOrders"));
const Profile = React.lazy(() => import("./pages/user/Profile/Profile"));
const MyBookings = React.lazy(() => import("./pages/user/MyBookings/MyBookings"));
const BookingConfirmation = React.lazy(() => import("./pages/user/BookingConfirmation/BookingConfirmation"));
const AdminAuth = React.lazy(() => import("./pages/admin/Auth/Auth"));
const AdminDashboard = React.lazy(() => import("./pages/admin/Dashboard/Dashboard"));
const AdminProducts = React.lazy(() => import("./pages/admin/Products/Products"));
const AdminServices = React.lazy(() => import("./pages/admin/Services/Services"));
const AdminBookings = React.lazy(() => import("./pages/admin/Bookings/Bookings"));
const AdminOrders = React.lazy(() => import("./pages/admin/Orders/Orders"));
const AdminContacts = React.lazy(() => import("./pages/admin/Contacts/Contacts"));
const AdminTimeSlots = React.lazy(() => import("./pages/admin/TimeSlots/TimeSlots"));
const NotFound = React.lazy(() => import("./pages/user/NotFound/NotFound"));
const NewBookingModal = React.lazy(() => import("./components/user/NewBookingModal/NewBookingModal"));

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <AuthProvider>
        <CartProvider>
          <BookingModalProvider>
          <Router>
            <React.Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/services" element={<Services />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/comparison" element={<Comparison />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/confirmation/:orderId" element={<OrderConfirmation />} />
                <Route path="/login" element={<UserAuth />} />
                <Route path="/register" element={<UserAuth />} />
                <Route path="/booking/confirmation/:id" element={<BookingConfirmation />} />
                <Route path="/admin/auth" element={<AdminAuth />} />
                <Route path="/admin/login" element={<AdminAuth />} />

                <Route element={<ProtectedRoute />}>
                  <Route path="/my-orders" element={<MyOrders />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/my-bookings" element={<MyBookings />} />
                </Route>

                <Route element={<AdminProtectedRoute />}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/products" element={<AdminProducts />} />
                  <Route path="/admin/services" element={<AdminServices />} />
                  <Route path="/admin/bookings" element={<AdminBookings />} />
                  <Route path="/admin/orders" element={<AdminOrders />} />
                  <Route path="/admin/contacts" element={<AdminContacts />} />
                  <Route path="/admin/timeslots" element={<AdminTimeSlots />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </React.Suspense>
            <ScrollUp />
            <React.Suspense fallback={null}>
              <NewBookingModal />
            </React.Suspense>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
            />
          </Router>
          </BookingModalProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
